import api from './api';
import { 
  Sensor, 
  SensorReading, 
  SensorReadingStats, 
  TTIRegistrationRequest, 
  TTIStatusResponse,
  TTIApplicationsListRequest,
  TTIApplicationsListResponse,
  TTIActivationRequest,
  GrowerSensorCreate,
  ImageDeviceDataResponse,
  TTIDeviceListRequest,
  TTIDeviceListResponse,
  ConnectTTIDeviceRequest,
  SensorGrowerAssignment,
  TTIDeviceListByAppRequest,
  ConnectTTIDeviceWithStoredKeyRequest,
  CSVColumnsResponse,
  CSVBatchImportResponse,
  HistoricalReadingsFetchResponse,
  ReadingsPurgeResponse
} from '../types';

export const sensorService = {
  async getSensorDefaults(): Promise<{ par_multiplier: number }> {
    const response = await api.get<{ par_multiplier: number }>('/api/sensors/defaults');
    return response.data;
  },

  async getSensors(growerId?: number): Promise<Sensor[]> {
    const params = growerId ? { grower_id: growerId } : {};
    const response = await api.get<Sensor[]>('/api/sensors/', { params });
    return response.data;
  },

  async getSensor(id: number): Promise<Sensor> {
    const response = await api.get<Sensor>(`/api/sensors/${id}`);
    return response.data;
  },

  async createSensor(data: Partial<Sensor>): Promise<Sensor> {
    const response = await api.post<Sensor>('/api/sensors/', data);
    return response.data;
  },

  async updateSensor(id: number, data: Partial<Sensor>): Promise<Sensor> {
    const response = await api.put<Sensor>(`/api/sensors/${id}`, data);
    return response.data;
  },

  async updateSensorLocation(id: number, location: string | null): Promise<Sensor> {
    const response = await api.patch<Sensor>(`/api/sensors/${id}/location`, { location });
    return response.data;
  },

  async deleteSensor(id: number): Promise<void> {
    await api.delete(`/api/sensors/${id}`);
  },

  async getReadings(
    sensorId: number,
    params?: {
      start_time?: string;
      end_time?: string;
      skip?: number;
      limit?: number;
    }
  ): Promise<SensorReading[]> {
    const response = await api.get<SensorReading[]>('/api/readings/', {
      params: { sensor_id: sensorId, ...params },
    });
    return response.data;
  },

  async getLatestReading(sensorId: number): Promise<SensorReading> {
    const response = await api.get<SensorReading>(`/api/readings/latest/${sensorId}`);
    return response.data;
  },

  async getLatestReadingsBulk(sensorIds: number[]): Promise<SensorReading[]> {
    if (sensorIds.length === 0) return [];
    const params = new URLSearchParams();
    sensorIds.forEach(id => params.append('sensor_ids', id.toString()));
    const response = await api.get<SensorReading[]>(`/api/readings/latest-bulk?${params.toString()}`);
    return response.data;
  },

  async getStats(
    sensorId: number,
    startTime?: string,
    endTime?: string
  ): Promise<SensorReadingStats> {
    const response = await api.get<SensorReadingStats>(`/api/readings/stats/${sensorId}`, {
      params: { start_time: startTime, end_time: endTime },
    });
    return response.data;
  },

  async createReading(data: Partial<SensorReading>): Promise<SensorReading> {
    const response = await api.post<SensorReading>('/api/readings/', data);
    return response.data;
  },

  // TTI Integration methods
  async registerWithTTI(sensorId: number, request: TTIRegistrationRequest): Promise<Sensor> {
    const response = await api.post<Sensor>(`/api/sensors/${sensorId}/register-with-tti`, request);
    return response.data;
  },

  async getTTIStatus(sensorId: number): Promise<TTIStatusResponse> {
    const response = await api.get<TTIStatusResponse>(`/api/sensors/${sensorId}/tti-status`);
    return response.data;
  },

  async unregisterFromTTI(sensorId: number): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`/api/sensors/${sensorId}/unregister-tti`);
    return response.data;
  },

  // New TTI features
  async listTTIApplications(request: TTIApplicationsListRequest): Promise<TTIApplicationsListResponse> {
    const response = await api.post<TTIApplicationsListResponse>('/api/sensors/tti/applications', request);
    return response.data;
  },

  async setTTIActivation(sensorId: number, request: TTIActivationRequest): Promise<Sensor> {
    const response = await api.post<Sensor>(`/api/sensors/${sensorId}/tti-activation`, request);
    return response.data;
  },

  // Grower self-service methods
  async createSensorAsGrower(data: GrowerSensorCreate): Promise<Sensor> {
    const response = await api.post<Sensor>('/api/sensors/grower-create', data);
    return response.data;
  },

  async getPendingSensors(): Promise<Sensor[]> {
    const response = await api.get<Sensor[]>('/api/sensors/pending');
    return response.data;
  },

  async approveSensor(sensorId: number): Promise<Sensor> {
    const response = await api.post<Sensor>(`/api/sensors/${sensorId}/approve`);
    return response.data;
  },

  async rejectSensor(sensorId: number): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(`/api/sensors/${sensorId}/reject`);
    return response.data;
  },

  // Image extraction
  async extractDeviceDataFromImage(file: File): Promise<ImageDeviceDataResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<ImageDeviceDataResponse>('/api/sensors/extract-from-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Connect existing TTI device methods
  async listTTIDevices(request: TTIDeviceListRequest): Promise<TTIDeviceListResponse> {
    const response = await api.post<TTIDeviceListResponse>('/api/sensors/tti/devices', request);
    return response.data;
  },

  async connectTTIDevice(request: ConnectTTIDeviceRequest): Promise<Sensor> {
    const response = await api.post<Sensor>('/api/sensors/connect-tti-device', request);
    return response.data;
  },

  // Sensor-Grower assignment methods
  async assignSensorToGrower(sensorId: number, assignment: SensorGrowerAssignment): Promise<Sensor> {
    const response = await api.patch<Sensor>(`/api/sensors/${sensorId}/assign-grower`, assignment);
    return response.data;
  },

  async getUnassignedSensors(): Promise<Sensor[]> {
    const response = await api.get<Sensor[]>('/api/sensors/unassigned');
    return response.data;
  },

  // Methods using stored API key
  async listTTIDevicesByApp(request: TTIDeviceListByAppRequest): Promise<TTIDeviceListResponse> {
    const response = await api.post<TTIDeviceListResponse>('/api/sensors/tti/devices-by-app', request);
    return response.data;
  },

  async connectTTIDeviceWithStoredKey(request: ConnectTTIDeviceWithStoredKeyRequest): Promise<Sensor> {
    const response = await api.post<Sensor>('/api/sensors/connect-tti-device-stored-key', request);
    return response.data;
  },

  // CSV Batch Import methods
  async getCSVColumns(): Promise<CSVColumnsResponse> {
    const response = await api.get<CSVColumnsResponse>('/api/sensors/tti/csv-columns');
    return response.data;
  },

  async batchRegisterFromCSV(file: File, appId: string): Promise<CSVBatchImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('app_id', appId);
    const response = await api.post<CSVBatchImportResponse>('/api/sensors/tti/batch-register', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Historical readings methods
  async fetchHistoricalReadings(sensorId: number, timePeriod: string): Promise<HistoricalReadingsFetchResponse> {
    const response = await api.post<HistoricalReadingsFetchResponse>(
      `/api/sensors/${sensorId}/fetch-historical-readings?time_period=${timePeriod}`
    );
    return response.data;
  },

  async purgeReadings(sensorId: number, timePeriod: string): Promise<ReadingsPurgeResponse> {
    const response = await api.delete<ReadingsPurgeResponse>(
      `/api/sensors/${sensorId}/readings?time_period=${timePeriod}`
    );
    return response.data;
  },
};

